import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import { useAttributiArticolo, useCreaArticolo } from '../api/creazione.js';
import { Modale } from '../componenti/Modale.js';
import { t } from '../i18n/lingua.js';

/**
 * Un articolo nuovo.
 *
 * Gli articoli arrivano da Business Central: questo serve per il caso raro —
 * un prodotto che il commerciale deve mettere subito in una proposta mentre
 * in BC non è ancora stato aperto.
 *
 * Il campo che conta è il **numero di Business Central**, non lo SKU: è
 * quello la chiave, ed è quello che tiene insieme l'articolo con gli
 * aggiornamenti che arrivano da BC. Se manca, l'articolo nasce lo stesso e
 * lo si dice: da BC non arriverà mai niente sopra, finché qualcuno non
 * scriverà il numero.
 */
export function ModaleNuovoArticolo({ onChiudi }: { onChiudi: () => void }) {
  const navigate = useNavigate();
  const crea = useCreaArticolo();
  const attributi = useAttributiArticolo();

  const [campi, setCampi] = useState({
    description: '',
    bcNumber: '',
    sku: '',
    alias: '',
    lineId: '',
    productId: '',
    typologyId: '',
    formatId: '',
    unitOfMeasurementId: '',
    notes: '',
  });

  const cambia = (chiave: keyof typeof campi, valore: string) =>
    setCampi((c) => ({ ...c, [chiave]: valore }));

  const errore = crea.error instanceof ErroreApi ? crea.error : null;
  const completo = campi.description.trim().length > 0;
  const numero = (v: string) => (v ? Number(v) : null);

  const conferma = () =>
    crea.mutate(
      {
        description: campi.description.trim(),
        bcNumber: campi.bcNumber.trim() || null,
        sku: campi.sku.trim() || null,
        alias: campi.alias.trim() || null,
        notes: campi.notes.trim() || null,
        lineId: numero(campi.lineId),
        productId: numero(campi.productId),
        typologyId: numero(campi.typologyId),
        formatId: numero(campi.formatId),
        unitOfMeasurementId: numero(campi.unitOfMeasurementId),
      },
      {
        onSuccess: ({ id }) => {
          onChiudi();
          void navigate({ to: '/items/$id', params: { id: String(id) } });
        },
      },
    );

  const tendina = (
    chiave: keyof typeof campi,
    etichetta: string,
    voci: { id: number; label: string }[] | undefined,
  ) => (
    <div className="field">
      <label htmlFor={`nart-${chiave}`}>{etichetta}</label>
      <select
        id={`nart-${chiave}`}
        className="select"
        value={campi[chiave]}
        onChange={(e) => cambia(chiave, e.target.value)}
      >
        <option value="">{t('Non specificato')}</option>
        {(voci ?? []).map((v) => (
          <option key={v.id} value={v.id}>
            {v.label}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <Modale
      titolo={t('Nuovo articolo')}
      nota={t(
        'Formati, pesi, imballo e codici GS1 si compilano dopo, dalla scheda: appena creato si apre da sola.',
      )}
      larga
      inCorso={crea.isPending}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn" onClick={onChiudi} disabled={crea.isPending}>
            {t('Annulla')}
          </button>
          <button
            className="btn btn-primary"
            onClick={conferma}
            disabled={!completo || crea.isPending}
          >
            {crea.isPending ? t('Creo…') : t('Crea')}
          </button>
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore.message}</span>
        </div>
      )}

      <div className="fields">
        <div className="field span-2">
          <label htmlFor="nart-desc">
            {t('Descrizione')}
            <span className="req">*</span>
          </label>
          <input
            id="nart-desc"
            className="input"
            value={campi.description}
            maxLength={200}
            autoFocus
            onChange={(e) => cambia('description', e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="nart-bc">{t('Numero BC')}</label>
          <input
            id="nart-bc"
            className="input num"
            value={campi.bcNumber}
            maxLength={50}
            onChange={(e) => cambia('bcNumber', e.target.value)}
          />
          <div className="hint">
            {t('È la chiave dell’articolo: senza, gli aggiornamenti da Business Central non lo troveranno.')}
          </div>
        </div>

        <div className="field">
          <label htmlFor="nart-sku">SKU</label>
          <input
            id="nart-sku"
            className="input num"
            value={campi.sku}
            maxLength={50}
            onChange={(e) => cambia('sku', e.target.value)}
          />
        </div>

        {tendina('lineId', t('Linea'), attributi.data?.lines)}
        {tendina('productId', t('Prodotto'), attributi.data?.products)}
        {tendina('typologyId', t('Tipologia'), attributi.data?.typologies)}
        {tendina('formatId', t('Formato'), attributi.data?.formats)}
        {tendina('unitOfMeasurementId', t('UdM'), attributi.data?.units)}

        <div className="field">
          <label htmlFor="nart-alias">{t('Descrizione estesa')}</label>
          <input
            id="nart-alias"
            className="input"
            value={campi.alias}
            maxLength={200}
            onChange={(e) => cambia('alias', e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label htmlFor="nart-note">{t('Annotazioni')}</label>
          <textarea
            id="nart-note"
            className="textarea"
            rows={3}
            value={campi.notes}
            onChange={(e) => cambia('notes', e.target.value)}
          />
        </div>
      </div>

      {campi.bcNumber.trim() === '' && (
        <div className="nota-prezzo" style={{ marginTop: 12 }}>
          {t(
            'Senza numero di Business Central l’articolo resta solo qui: si può mettere in un listino e in un ordine, ma l’importazione da BC non lo riconoscerà. Il numero di Business Central nella scheda non si cambia: se l’articolo c’è già in BC, scrivilo adesso.',
          )}
        </div>
      )}
    </Modale>
  );
}
